import { makeStyles } from '@material-ui/styles'
import type Theme from 'pf9-ui-components/built/theme-manager/themes/model'
import React, { useEffect, useMemo } from 'react'
import { useAppSelector } from 'app/store'
import type { ArrayElement } from 'core/actions/Action'
import SimpleLink from 'core/components/SimpleLink'
import ListContainer from 'core/containers/ListContainer'
import Button from 'core/elements/button/Button'
import type { GridViewColumn } from 'core/elements/grid/Grid'
import GridSearchFilter from 'core/elements/grid/GridSearchFilter'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import { routeManager } from 'core/utils/routes'
import { listByoHosts } from 'k8s/components/nodes/byoh/actions'
import { physicalNodeSelector, UNASSIGNED_CLUSTER_NAME } from 'k8s/components/nodes/byoh/selectors'
import type DataKeys from 'k8s/DataKeys'
import { noop } from 'utils/fp'

const useVmExpandedRowStyles = makeStyles((theme: Theme) => ({
  emptyStateContainer: {
    textAlign: 'center',
  },
  hostButton: {
    '& > span': { color: theme.palette.common.white, fontWeight: 'bold' },
  },
}))

type SelectorModel = ArrayElement<ReturnType<typeof physicalNodeSelector>>

const columns: GridViewColumn<SelectorModel>[] = [
  { key: 'name', label: 'Name' },
  {
    key: 'ipAddresses',
    label: 'IP Addresses',
    CellComponent: ({ value }) => {
      return (
        <>
          {value.map((iface) => (
            <Text key={iface} variant="body2">
              {iface}
            </Text>
          ))}
        </>
      )
    },
  },
  {
    key: 'status.hostinfo.osimage',
    label: 'OS Image',
  },
  {
    key: 'status.hostinfo.osname',
    label: 'OS Name',
  },
]

const EMPTY_MASTER_NODES: Array<{ name: string }> = []

const nameSearchFilter = [
  {
    key: 'search',
    equalityComparerFn: (item: SelectorModel, value: string) =>
      String(item.name).toLocaleLowerCase().includes(value.toLocaleLowerCase()),
    FilterComponent: GridSearchFilter,
  },
]

/**
 * @summary PhysicalNodesTable component
 * @param selectedNodes - Selected nodes
 * @param getParamsUpdater - Function to update params
 * @returns PhysicalNodesTable component is used to display the list of physical nodes for cluster creation or adding nodes to existing cluster
 */
export default function PhysicalNodesTable({
  selectedNodes,
  masterPhysicalNodes = EMPTY_MASTER_NODES,
  setError = undefined,
  label = 'Worker Nodes',
  disabled = false,
  onSelectChange = noop,
}) {
  const classes = useVmExpandedRowStyles()

  const { loading: loadingPhysicalNodes, reload } = useListAction(listByoHosts)
  const data = useAppSelector(physicalNodeSelector)
  // const data = []
  const filteredData = useMemo(
    () =>
      data?.filter((node) => {
        return (
          node?.clusterName === UNASSIGNED_CLUSTER_NAME &&
          !masterPhysicalNodes.some((n) => n.name === node.name)
        )
      }),
    [data, masterPhysicalNodes],
  )

  // Set initial load
  useEffect(() => {
    reload(true)
  }, [])

  useEffect(() => {
    if (loadingPhysicalNodes) return

    if (filteredData?.length === 0 && setError) {
      setError({
        title: `No ${label} available`,
      })
    }
    if (filteredData?.length > 0 && setError) {
      setError(null)
    }
  }, [filteredData, loadingPhysicalNodes, label])

  return (
    <div data-testid="physical-nodes-table">
      <ListContainer<DataKeys.KaapiBYOHosts, SelectorModel>
        searchTargets={['name']}
        globalFilters={nameSearchFilter}
        uniqueIdentifier="uid"
        columns={columns}
        data={filteredData}
        label={label}
        orderBy="name"
        orderDirection="asc"
        emptyContent={
          <div className={classes.emptyStateContainer}>
            <Text>No {label} available</Text>
            <SimpleLink
              src={routeManager.routes.kubernetes?.infrastructure?.addPhysicalHosts?.path()}
            >
              Add Physical Host
            </SimpleLink>
          </div>
        }
        disableRowSelection={false}
        disableColumnHiding
        disableSorting
        disableSearch
        multiSelection
        selectedItems={selectedNodes}
        onSelectChange={onSelectChange}
        rowIsSelectableFn={(item) => !disabled}
        loading={loadingPhysicalNodes}
        loadingMessage={`Loading ${label}...`}
        extraToolbarContent={
          <Button variant="primary">
            <SimpleLink
              className={classes.hostButton}
              src={routeManager.routes.kubernetes?.infrastructure?.addPhysicalHosts?.path()}
            >
              Add physical hosts
            </SimpleLink>
          </Button>
        }
      />
    </div>
  )
}
